"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { CompactMenu, type CompactMenuItem } from "@/components/compact-menu";
import { useAuth } from "@/components/auth/Auth";
import CommonConfirmationModal from "@/components/utils/modal/CommonConfirmationModal";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveCuration } from "@/generated/models/ApiWaveCuration";
import type { DropCurationMembership } from "@/hooks/drops/useDropCurations";
import { invalidateProfileWaveQueries } from "@/hooks/useProfileWave";
import { getWaveCurationsQueryKey } from "@/hooks/waves/useWaveCurations";
import { useProfileWaveMutation } from "@/hooks/useProfileWaveMutation";
import { commonApiDelete } from "@/services/api/common-api";
import MyStreamWaveCurationCreateDialog from "./MyStreamWaveCurationCreateDialog";
interface MyStreamWaveCurationTabMenuProps {
  readonly wave: ApiWave;
  readonly curation: ApiWaveCuration;
  readonly onDeleted?: (() => void) | undefined;
  readonly canSetAsProfileCuration?: boolean | undefined;
  readonly isSetAsProfileCurationPending?: boolean | undefined;
}
const getErrorMessage = (error: unknown): string => error instanceof Error ? error.message : "Failed to delete curation.";
export default function MyStreamWaveCurationTabMenu(t0) {
  const $ = _c(47);
  const {
    wave,
    curation,
    onDeleted,
    canSetAsProfileCuration: t1,
    isSetAsProfileCurationPending: t2
  } = t0;
  const canSetAsProfileCuration = t1 === undefined ? false : t1;
  const isSetAsProfileCurationPending = t2 === undefined ? false : t2;
  const queryClient = useQueryClient();
  const {
    connectedProfile,
    requestAuth,
    setToast
  } = useAuth();
  const {
    updateProfileWave,
    isPending: isProfileWavePending
  } = useProfileWaveMutation(connectedProfile);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const isSettingProfileCuration = isSetAsProfileCurationPending || isProfileWavePending;
  let t3;
  if ($[0] !== curation.id || $[1] !== requestAuth || $[2] !== wave) {
    t3 = async () => {
      const auth = await requestAuth();
      if (!auth.success) {
        throw new Error("Authentication was cancelled.");
      }
      await commonApiDelete({
        endpoint: `waves/${wave.id}/curations/${curation.id}`
      });
    };
    $[0] = curation.id;
    $[1] = requestAuth;
    $[2] = wave;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== connectedProfile || $[5] !== curation.id || $[6] !== onDeleted || $[7] !== queryClient || $[8] !== setToast || $[9] !== wave) {
    t4 = async () => {
      queryClient.setQueryData(getWaveCurationsQueryKey(wave.id), current => current?.filter(item => item.id !== curation.id));
      queryClient.setQueriesData({
        queryKey: ["drop-curations"]
      }, current_0 => current_0?.filter(item_0 => item_0.id !== curation.id));
      await queryClient.invalidateQueries({
        queryKey: getWaveCurationsQueryKey(wave.id)
      });
      await queryClient.invalidateQueries({
        queryKey: ["drop-curations"]
      });
      await invalidateProfileWaveQueries(queryClient, [connectedProfile, wave.author]);
      setToast({
        type: "success",
        message: "Curation deleted."
      });
      setIsDeleteOpen(false);
      onDeleted?.();
    };
    $[4] = connectedProfile;
    $[5] = curation.id;
    $[6] = onDeleted;
    $[7] = queryClient;
    $[8] = setToast;
    $[9] = wave;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== setToast) {
    t5 = error => {
      setToast({
        type: "error",
        message: getErrorMessage(error)
      });
    };
    $[11] = setToast;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = {
      mutationFn: t3,
      onSuccess: t4,
      onError: t5
    };
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const deleteMutation = useMutation(t6);
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      id: "edit",
      label: "Edit curation",
      onSelect: () => setIsEditOpen(true)
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== canSetAsProfileCuration || $[19] !== curation.id || $[20] !== isSettingProfileCuration || $[21] !== updateProfileWave || $[22] !== wave) {
    t8 = canSetAsProfileCuration ? [{
      id: "set-profile-curation",
      label: "Set as profile curation",
      onSelect: () => {
        updateProfileWave(wave.id, curation.id);
      },
      disabled: isSettingProfileCuration
    }] : [];
    $[18] = canSetAsProfileCuration;
    $[19] = curation.id;
    $[20] = isSettingProfileCuration;
    $[21] = updateProfileWave;
    $[22] = wave;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      id: "delete",
      label: "Delete curation",
      onSelect: () => setIsDeleteOpen(true),
      className: "tw-text-red desktop-hover:hover:tw-text-red"
    };
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t8) {
    t10 = [t7, ...t8, t9];
    $[25] = t8;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const menuItems = t10;
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <EllipsisVerticalIcon className="tw-mt-0.5 tw-block tw-size-4 tw-flex-shrink-0" />;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const t12 = deleteMutation.isPending || isSettingProfileCuration;
  let t13;
  if ($[28] !== menuItems || $[29] !== t12) {
    t13 = <CompactMenu triggerClassName="tw-inline-flex tw-h-8 tw-w-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-text-iron-400 tw-transition hover:tw-text-iron-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-40" trigger={t11} aria-label="Curation options" items={menuItems} menuWidthClassName="tw-w-52" disabled={t12} />;
    $[28] = menuItems;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== curation || $[32] !== isEditOpen || $[33] !== wave) {
    t14 = isEditOpen && <MyStreamWaveCurationCreateDialog wave={wave} isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} onSaved={_temp} curation={curation} />;
    $[31] = curation;
    $[32] = isEditOpen;
    $[33] = wave;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsDeleteOpen(false);
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== deleteMutation) {
    t16 = () => deleteMutation.mutate();
    $[36] = deleteMutation;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const t17 = `Delete "${curation.name}" from this wave?`;
  let t18;
  if ($[38] !== deleteMutation.isPending || $[39] !== isDeleteOpen || $[40] !== t16 || $[41] !== t17) {
    t18 = <CommonConfirmationModal isOpen={isDeleteOpen} onClose={t15} onConfirm={t16} title="Delete curation" message={t17} confirmText="Delete" isConfirming={deleteMutation.isPending} />;
    $[38] = deleteMutation.isPending;
    $[39] = isDeleteOpen;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t13 || $[44] !== t14 || $[45] !== t18) {
    t19 = <>{t13}{t14}{t18}</>;
    $[43] = t13;
    $[44] = t14;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
}
function _temp() {}